import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import CommonButton from '../../components/Buttons/CommonButton';
import Header from '../../components/Header/Header';
import Icon from '../../components/Icon/Icon';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import ModalBody from '../../components/Modal/ModalBody/ModalBody';
import ModalFooter from '../../components/Modal/ModalFooter/ModalFooter';
import ModalHeader from '../../components/Modal/ModalHeader/ModalHeader';
import { formatBytesAsMb } from '../../components/Campaign/WizardCreatives/CreativesContainer/creativeWeight';
import { getScreensByExternalIds } from '../../services/screensService';
import statsService from '../../services/statsService';
import { notificationsActions } from '../../store';
import {
    buildGoogleMapsUrl,
    formatCampaignAuditRecordDate,
    formatCreativeDuration,
    getCreativeTypeLabel,
    isHtmlCreative,
    isVideoCreative,
    getCampaignAuditAliases,
    getCampaignAuditEndpointScreenId,
    getCampaignAuditHourRange,
    getCampaignAuditLookupIds,
    normalizeCampaignAuditScreens,
    shouldFetchScreenDetails,
    toInlineScreenImageUrl,
} from '../../utils/campaignAudit';
import { canAccessAdminFeatures } from '../../utils/authPermissions';
import { getStrategyTranslationKey } from '../../utils/strategy';
import {
    campaignStatus,
    formatDateAccordingToBrowser,
    formatNumber,
} from '../../utils/functions';
import { useAuth } from '../UseAuth';
import { useService } from '../UseService';
import styles from './CampaignAuditPage.module.css';

const isActiveCampaign = (campaign) =>
    campaign?.status === campaignStatus('ACTIVE') ||
    campaign?.status === 'ACTIVE';

const AuditMetricTooltip = ({ records, timezone, t, showCampaign = true }) => (
    <div className={styles.metricTooltip}>
        {records.map((record, index) => (
            <div
                key={`${record.campaignId || 'campaign'}-${record._ts || index}`}
                className={styles.metricTooltipRow}>
                {showCampaign && (
                    <div className={styles.metricTooltipCampaign}>
                        {t('sections.dashboard-individual.audit.tooltip-campaign', {
                            campaign: record.campaignId || '-',
                        })}
                    </div>
                )}
                <div className={styles.metricTooltipDate}>
                    {formatCampaignAuditRecordDate(record._ts, timezone)}
                </div>
            </div>
        ))}
    </div>
);

const AuditMetric = ({
    label,
    value,
    records = [],
    timezone,
    t,
    showCampaignInTooltip = true,
}) => {
    const hasRecords = records.length > 0;

    return (
        <div
            className={`rounded border border-gray-100 bg-gray-50 p-3 ${
                hasRecords ? styles.metricCardWithTooltip : ''
            }`}
            tabIndex={hasRecords ? 0 : undefined}>
            <div className="text-xs font-light uppercase text-gray-500">
                {label}
            </div>
            <div className="mt-1 text-xl font-semibold text-gray-900">
                {formatNumber(value, 'rounded')}
            </div>
            {hasRecords && (
                <AuditMetricTooltip
                    records={records}
                    timezone={timezone}
                    t={t}
                    showCampaign={showCampaignInTooltip}
                />
            )}
        </div>
    );
};

const SkeletonBlock = ({ className = '' }) => (
    <div className={`${styles.skeletonBlock} ${className}`} />
);

const ScreenListSkeleton = () => (
    <div className="space-y-2">
        {[0, 1, 2, 3, 4, 5].map((item) => (
            <div
                key={item}
                className="rounded border border-gray-100 bg-white px-3 py-3">
                <div className="flex items-center gap-2">
                    <SkeletonBlock className="h-4 w-4 rounded" />
                    <div className="min-w-0 flex-1">
                        <SkeletonBlock className="mb-2 h-4 w-11/12" />
                        <SkeletonBlock className="h-3 w-20" />
                    </div>
                    <SkeletonBlock className="h-8 w-8 rounded-full" />
                </div>
            </div>
        ))}
    </div>
);

const AuditMetricsSkeleton = () => (
    <>
        <SkeletonBlock className="mb-3 h-16 w-full rounded" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
                <div
                    key={item}
                    className="rounded border border-gray-100 bg-gray-50 p-3">
                    <SkeletonBlock className="mb-3 h-3 w-24" />
                    <SkeletonBlock className="h-7 w-16" />
                </div>
            ))}
        </div>
    </>
);

const CreativeDetail = ({ label, value }) => (
    <div className="min-w-0">
        <div className="text-xs font-light uppercase text-gray-500">{label}</div>
        <div className="truncate text-sm font-semibold text-gray-900" title={value}>
            {value}
        </div>
    </div>
);

const CREATIVE_PREVIEW_WIDTH = 128;
const CREATIVE_PREVIEW_HEIGHT = 80;

// Renders the HTML creative at its real size and scales it down so the whole
// creative fits the preview box (a small iframe would only show its corner).
// Dynamic creatives are stored without size and play at the screen size.
const HtmlCreativePreview = ({ creative, screenSize }) => {
    const hasCreativeSize =
        Number(creative.width) > 0 && Number(creative.height) > 0;
    const width = Number(hasCreativeSize ? creative.width : screenSize?.width);
    const height = Number(
        hasCreativeSize ? creative.height : screenSize?.height
    );
    const hasSize = width > 0 && height > 0;
    const scale = hasSize
        ? Math.min(
              CREATIVE_PREVIEW_WIDTH / width,
              CREATIVE_PREVIEW_HEIGHT / height
          )
        : 1;

    return (
        <iframe
            src={creative.url}
            title={creative.filename || creative.creativeId}
            // allow-same-origin: dynamic creatives (Next.js apps) read
            // localStorage / document.cookie on boot, which throws in an
            // opaque-origin sandbox and leaves them black. The creative keeps
            // its own origin, so it still cannot reach the DSP page.
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
            scrolling="no"
            className="pointer-events-none shrink-0 border-0 bg-white"
            style={
                hasSize
                    ? {
                          width,
                          height,
                          transform: `scale(${scale})`,
                          transformOrigin: 'center',
                      }
                    : { width: '100%', height: '100%' }
            }
        />
    );
};

const AuditCreativeCard = ({ creative, screenSize, t }) => {
    const isHtml = isHtmlCreative(creative);
    const isVideo = !isHtml && isVideoCreative(creative);
    // The stored duration may be truncated for creatives uploaded before it
    // was saved as a decimal, so the video metadata takes precedence.
    const [metadataDuration, setMetadataDuration] = useState(null);
    const duration = metadataDuration ?? creative.durationSeconds;
    const resolution =
        creative.width && creative.height
            ? `${creative.width} x ${creative.height}`
            : '-';

    useEffect(() => {
        setMetadataDuration(null);
    }, [creative.url]);

    return (
        <div
            className={`flex gap-3 rounded border p-3 ${
                creative.isDefaultForPurchase
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-100 bg-gray-50'
            }`}>
            <div className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-200">
                {isHtml && creative.url ? (
                    <HtmlCreativePreview
                        creative={creative}
                        screenSize={screenSize}
                    />
                ) : isVideo && creative.url ? (
                    <video
                        src={creative.url}
                        poster={creative.thumbnailUrl || undefined}
                        preload="metadata"
                        muted
                        controls
                        className="h-full w-full object-contain"
                        onLoadedMetadata={(event) => {
                            const value = event.currentTarget.duration;
                            if (Number.isFinite(value) && value > 0) {
                                setMetadataDuration(value);
                            }
                        }}
                    />
                ) : creative.thumbnailUrl || creative.url ? (
                    <img
                        src={creative.thumbnailUrl || creative.url}
                        alt={creative.filename || creative.creativeId}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <Icon name="image" size="lg" className="text-gray-400" />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span
                        className="truncate text-sm font-semibold text-gray-900"
                        title={creative.filename || creative.creativeId}>
                        {creative.filename || creative.creativeId}
                    </span>
                    {creative.isDefaultForPurchase && (
                        <span className="rounded bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                            {t('sections.dashboard-individual.audit.creative-default')}
                        </span>
                    )}
                    {!creative.sizeMatch && (
                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                            {t(
                                'sections.dashboard-individual.audit.creative-size-mismatch'
                            )}
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                    <CreativeDetail
                        label={t('sections.dashboard-individual.audit.creative-type')}
                        value={getCreativeTypeLabel(creative)}
                    />
                    <CreativeDetail
                        label={t('sections.dashboard-individual.audit.creative-weight')}
                        value={formatBytesAsMb(creative.sizeBytes) || '-'}
                    />
                    <CreativeDetail
                        label={t(
                            'sections.dashboard-individual.audit.creative-resolution'
                        )}
                        value={resolution}
                    />
                    {isVideo && (
                        <CreativeDetail
                            label={t(
                                'sections.dashboard-individual.audit.creative-duration'
                            )}
                            value={formatCreativeDuration(duration)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const AuditCreativesPanel = ({ creatives, screenSize, isLoading, error, t }) => (
    <div className="mt-5">
        <h3 className="mb-3 text-base font-semibold text-gray-900">
            {t('sections.dashboard-individual.audit.creatives-title')}
        </h3>
        {isLoading ? (
            <SkeletonBlock className="h-24 w-full rounded" />
        ) : error ? (
            <div className="rounded border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                {error}
            </div>
        ) : creatives.length === 0 ? (
            <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                {t('sections.dashboard-individual.audit.creatives-empty')}
            </div>
        ) : (
            <div className="space-y-2">
                {creatives.map((creative) => (
                    <AuditCreativeCard
                        key={creative.creativeId}
                        creative={creative}
                        screenSize={screenSize}
                        t={t}
                    />
                ))}
            </div>
        )}
    </div>
);

const getLossReasonTranslationCode = (lossReason) => {
    const code = Number(lossReason?.code);
    const knownCodes = [400, 401, 402, 403, 404, 405, 406, 407, 499];

    return knownCodes.includes(code) ? code : 499;
};

const MANUAL_PURCHASE_MODES = [1, 4, 5];
const AUDIT_AUTO_REFRESH_SECONDS = 10;

const getPurchaseModesLabel = (t) =>
    MANUAL_PURCHASE_MODES.map((mode) =>
        t(getStrategyTranslationKey({ strategy: mode }))
    ).join(', ');

const getPurchaseModeWarning = (campaign, t) => {
    const mode = Number(campaign?.scheduler_mode ?? campaign?.schedulerMode);
    if (MANUAL_PURCHASE_MODES.includes(mode)) return '';

    const strategyKey = getStrategyTranslationKey({ strategy: mode });
    const modes = getPurchaseModesLabel(t);
    return strategyKey
        ? t('sections.dashboard-individual.audit.purchase-mode-unavailable', {
              strategy: t(strategyKey),
              modes,
          })
        : t('sections.dashboard-individual.audit.purchase-error-unsupported-mode', {
              modes,
          });
};

const getPurchaseErrorMessage = (response, t, selectedScreen, modeWarning) => {
    if (
        response?.code === 'UNSUPPORTED_SCHEDULER_MODE' ||
        (typeof response?.message === 'string' &&
            /^Manual schedule injection only supports scheduler(?:_mode| modes) \d+(?:,\s*\d+)*\.?$/i.test(
                response.message.trim()
            ))
    ) {
        return (
            modeWarning ||
            t('sections.dashboard-individual.audit.purchase-error-unsupported-mode', {
                modes: getPurchaseModesLabel(t),
            })
        );
    }

    const loadErrorTranslations = {
        MANUAL_CAMPAIGN_LOAD_FAILED: 'purchase-error-campaign-load',
        MANUAL_CAMPAIGN_CACHE_CHANGED: 'purchase-error-scheduler-refresh',
    };
    const loadErrorKey =
        loadErrorTranslations[response?.code] ||
        loadErrorTranslations[response?.message];
    if (loadErrorKey) {
        return t(`sections.dashboard-individual.audit.${loadErrorKey}`);
    }

    if (
        typeof response?.message === 'string' &&
        /^Campaign \d+ is not present in scheduler cache$/i.test(
            response.message.trim()
        )
    ) {
        return t(
            'sections.dashboard-individual.audit.purchase-error-campaign-not-scheduled'
        );
    }

    if (response?.code === 'CREATIVES_NOT_APPROVED_FOR_SCREEN') {
        return t(
            'sections.dashboard-individual.audit.purchase-error-creatives-not-approved',
            { screen: selectedScreen?.auditName || '-' }
        );
    }

    if (
        response?.code === 'CREATIVE_SIZE_MISMATCH' ||
        response?.message === 'CREATIVE_SIZE_MISMATCH'
    ) {
        const details = Array.isArray(response?.details)
            ? response.details.find(
                  (item) => item?.screen && Array.isArray(item?.creatives)
              )
            : response?.details;
        const screenSize = details?.screen
            ? `${details.screen.width}x${details.screen.height}`
            : '';
        const creativeSizes = [
            ...new Set(
                (details?.creatives || []).map(
                    (creative) => `${creative.width}x${creative.height}`
                )
            ),
        ].join(', ');

        if (screenSize && creativeSizes) {
            return t(
                'sections.dashboard-individual.audit.purchase-error-creative-size-mismatch',
                { screenSize, creativeSizes }
            );
        }
    }

    return (
        response?.message ||
        t('sections.dashboard-individual.audit.purchase-error-description', {
            screen: selectedScreen?.auditName || '-',
        })
    );
};

const LossReasonsPanel = ({ lossReasons, t }) => (
    <div className="mb-3 rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-950">
        <div className="mb-3 flex items-start">
            <Icon
                name="exclamation-triangle"
                size="sm"
                className="mr-2 mt-1 text-yellow-500"
            />
            <div className="min-w-0">
                <div className="font-semibold">
                    {t('sections.dashboard-individual.audit.loss-reasons-title')}
                </div>
                <div className="mt-1 text-xs text-yellow-700">
                    {t('sections.dashboard-individual.audit.loss-reasons-description')}
                </div>
            </div>
        </div>
        <div className={styles.lossReasonsGrid}>
            {lossReasons.map((lossReason) => {
                const translationCode = getLossReasonTranslationCode(lossReason);
                const translationPath = `sections.dashboard-individual.audit.loss-reason-details.${translationCode}`;
                const title = t(`${translationPath}.title`, {
                    defaultValue:
                        lossReason.title ||
                        lossReason.message ||
                        lossReason.reason ||
                        '-',
                });
                const description = t(`${translationPath}.description`, {
                    defaultValue: lossReason.description || '',
                });

                return (
                    <div
                        key={`${lossReason.code}-${lossReason.reason}`}
                        className={styles.lossReasonItem}>
                        <div className="flex items-start justify-between gap-2">
                            <h4 className="min-w-0 text-sm font-semibold leading-5 text-gray-900">
                                {title}
                            </h4>
                            <span className={styles.lossReasonCount}>
                                {lossReason.count}
                            </span>
                        </div>
                        {description && (
                            <p className="mt-2 text-xs leading-5 text-gray-700">
                                {description}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    </div>
);

const formatCampaignDate = (date) => {
    if (!date) return '-';
    const parsedDate = new Date(Number(date) || date);
    return formatDateAccordingToBrowser(parsedDate);
};

function CampaignAuditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const history = useHistory();
    const auth = useAuth();
    const service = useService();
    const dispatch = useDispatch();
    const dashboard = useSelector((state) => state.dashboard);
    const campaign = dashboard.campaign;
    const purchaseModeWarning = getPurchaseModeWarning(campaign, t);
    const currencies = useSelector((state) => state.wallet.currencies);
    const timezones = useSelector((state) => state.extra.timezones);
    const [fetchedScreens, setFetchedScreens] = useState([]);
    const [isFetchingScreens, setIsFetchingScreens] = useState(false);
    const [selectedScreenId, setSelectedScreenId] = useState('');
    const [screenSearch, setScreenSearch] = useState('');
    const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
    const [auditData, setAuditData] = useState(null);
    const [auditDataError, setAuditDataError] = useState('');
    const [isFetchingAuditData, setIsFetchingAuditData] = useState(false);
    const [screenCreatives, setScreenCreatives] = useState([]);
    const [screenCreativesScreenSize, setScreenCreativesScreenSize] =
        useState(null);
    const [screenCreativesError, setScreenCreativesError] = useState('');
    const [isFetchingScreenCreatives, setIsFetchingScreenCreatives] =
        useState(false);
    const [now, setNow] = useState(() => new Date());
    const [refreshCount, setRefreshCount] = useState(0);
    const [auditPollCount, setAuditPollCount] = useState(0);
    const [secondsToAuditRefresh, setSecondsToAuditRefresh] = useState(
        AUDIT_AUTO_REFRESH_SECONDS
    );
    const [isAuditRequestInFlight, setIsAuditRequestInFlight] = useState(false);
    const [isPageVisible, setIsPageVisible] = useState(() => !document.hidden);
    const silentAuditRefreshRef = useRef(false);
    const [isGeneratingPurchase, setIsGeneratingPurchase] = useState(false);

    const campaignLoaded = Boolean(campaign?.id);
    const isSameCampaign = String(campaign?.id || '') === String(id || '');
    const isActive = isActiveCampaign(campaign);

    useEffect(() => {
        setScreenSearch('');
    }, [id]);

    useEffect(() => {
        if (id && (!campaignLoaded || !isSameCampaign) && currencies.length > 0) {
            service.getCampaignPreview(id);
        }
    }, [id, campaignLoaded, isSameCampaign, currencies.length]);

    useEffect(() => {
        if (campaignLoaded && isSameCampaign && !isActive) {
            history.replace(`/dashboard/${id}`);
        }
    }, [campaignLoaded, isSameCampaign, isActive, history, id]);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 60_000);

        return () => clearInterval(interval);
    }, []);

    const timezone = useMemo(
        () =>
            timezones.find((item) => item.id === campaign?.timezone_id) || {},
        [timezones, campaign?.timezone_id]
    );

    const campaignScreens = useMemo(() => {
        if (Array.isArray(campaign?.screen)) return campaign.screen;
        if (Array.isArray(campaign?.screens)) return campaign.screens;
        return [];
    }, [campaign?.screen, campaign?.screens]);

    const idsToFetch = useMemo(
        () =>
            getCampaignAuditLookupIds(
                campaignScreens.filter(shouldFetchScreenDetails)
            ),
        [campaignScreens]
    );

    useEffect(() => {
        let mounted = true;

        const fetchScreens = async () => {
            if (!isActive || idsToFetch.length === 0) {
                setFetchedScreens([]);
                return;
            }

            setIsFetchingScreens(true);
            try {
                const screens = await getScreensByExternalIds(idsToFetch);
                if (mounted) setFetchedScreens(screens);
            } catch (error) {
                console.error('Campaign audit screens fetch failed:', error);
                if (mounted) setFetchedScreens([]);
            } finally {
                if (mounted) setIsFetchingScreens(false);
            }
        };

        fetchScreens();

        return () => {
            mounted = false;
        };
    }, [idsToFetch, isActive, refreshCount]);

    const auditScreens = useMemo(
        () => normalizeCampaignAuditScreens(campaignScreens, fetchedScreens),
        [campaignScreens, fetchedScreens]
    );

    const normalizedScreenSearch = screenSearch.trim().toLowerCase();
    const filteredAuditScreens = useMemo(
        () =>
            auditScreens.filter((screen) =>
                [screen.auditName, screen.auditId].some((value) =>
                    String(value ?? '')
                        .toLowerCase()
                        .includes(normalizedScreenSearch)
                )
            ),
        [auditScreens, normalizedScreenSearch]
    );

    useEffect(() => {
        if (auditScreens.length === 0) {
            setSelectedScreenId('');
            return;
        }

        const selectedStillExists = auditScreens.some(
            (screen) => screen.auditId === selectedScreenId
        );

        if (!selectedStillExists) {
            setSelectedScreenId(auditScreens[0].auditId);
        }
    }, [auditScreens, selectedScreenId]);

    const selectedScreen = useMemo(
        () =>
            auditScreens.find((screen) => screen.auditId === selectedScreenId) ||
            auditScreens[0],
        [auditScreens, selectedScreenId]
    );

    const hourRange = useMemo(
        () => getCampaignAuditHourRange(timezone, now),
        [timezone, now]
    );

    const auditEndpointScreenId = useMemo(
        () =>
            selectedScreen
                ? getCampaignAuditEndpointScreenId(selectedScreen)
                : '',
        [selectedScreen]
    );

    const auditAliases = useMemo(
        () =>
            selectedScreen
                ? getCampaignAuditAliases(selectedScreen)
                : [],
        [selectedScreen]
    );

    const auditAliasesKey = auditAliases.join('|');
    const auditRequestConfig = useMemo(() => {
        const adminSession = auth.getAdminSessionFromLocalStorage?.();

        if (
            !auth.isSupervising?.() ||
            !canAccessAdminFeatures(auth) ||
            !adminSession?.token
        ) {
            return undefined;
        }

        return {
            headers: {
                Authorization: `Bearer ${adminSession.token}`,
            },
        };
    }, [auth]);

    useEffect(() => {
        let mounted = true;

        // Auto-refresh polls reload the metrics in place: no skeleton, and a
        // failed poll keeps the last metrics instead of replacing them.
        const isSilentRefresh = silentAuditRefreshRef.current;
        silentAuditRefreshRef.current = false;

        const fetchAuditData = async () => {
            if (!isActive || !selectedScreen) {
                setAuditData(null);
                setAuditDataError('');
                setIsFetchingAuditData(false);
                setIsAuditRequestInFlight(false);
                return;
            }

            if (!auditEndpointScreenId) {
                setAuditData(null);
                setAuditDataError('');
                setIsAuditRequestInFlight(false);

                if (isFetchingScreens && shouldFetchScreenDetails(selectedScreen)) {
                    setIsFetchingAuditData(true);
                    return;
                }

                setIsFetchingAuditData(false);
                setAuditDataError(
                    t('sections.dashboard-individual.audit.screen-id-unavailable')
                );
                return;
            }

            setIsAuditRequestInFlight(true);
            if (!isSilentRefresh) {
                setIsFetchingAuditData(true);
                setAuditDataError('');
            }

            const response = await statsService.getCampaignScreenAudit({
                campaignId: id,
                screenId: auditEndpointScreenId,
                fromUtc: hourRange.utcFrom,
                aliases: auditAliases,
                requestConfig: auditRequestConfig,
            });

            if (!mounted) return;

            if (response?.error) {
                if (!isSilentRefresh) {
                    setAuditData(null);
                    setAuditDataError(
                        response.message ||
                            t('sections.dashboard-individual.audit.audit-load-error')
                    );
                }
            } else {
                setAuditData(response?.data || null);
                setAuditDataError('');
            }

            setIsFetchingAuditData(false);
            setIsAuditRequestInFlight(false);
        };

        fetchAuditData();

        return () => {
            mounted = false;
        };
    }, [
        id,
        isActive,
        selectedScreen,
        auditEndpointScreenId,
        auditAliasesKey,
        auditRequestConfig,
        isFetchingScreens,
        hourRange.utcFrom,
        refreshCount,
        auditPollCount,
        t,
    ]);

    const canAutoRefreshAudit =
        isActive &&
        Boolean(auditEndpointScreenId) &&
        isPageVisible &&
        !isAuditRequestInFlight &&
        !isFetchingScreens;

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsPageVisible(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () =>
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
    }, []);

    useEffect(() => {
        setSecondsToAuditRefresh(AUDIT_AUTO_REFRESH_SECONDS);
    }, [auditEndpointScreenId]);

    // Countdown to the next metrics poll. It is paused while a request is in
    // flight, so it restarts once the previous response arrived.
    useEffect(() => {
        if (!canAutoRefreshAudit) return undefined;

        if (secondsToAuditRefresh <= 0) {
            silentAuditRefreshRef.current = true;
            setNow(new Date());
            setAuditPollCount((current) => current + 1);
            setSecondsToAuditRefresh(AUDIT_AUTO_REFRESH_SECONDS);
            return undefined;
        }

        const timeout = setTimeout(() => {
            setSecondsToAuditRefresh((current) => current - 1);
        }, 1000);

        return () => clearTimeout(timeout);
    }, [canAutoRefreshAudit, secondsToAuditRefresh]);

    useEffect(() => {
        let mounted = true;

        const fetchScreenCreatives = async () => {
            setScreenCreatives([]);
            setScreenCreativesScreenSize(null);
            setScreenCreativesError('');

            if (!isActive || !auditEndpointScreenId) {
                setIsFetchingScreenCreatives(false);
                return;
            }

            setIsFetchingScreenCreatives(true);

            const response = await statsService.getCampaignScreenCreatives({
                campaignId: id,
                screenId: auditEndpointScreenId,
                requestConfig: auditRequestConfig,
            });

            if (!mounted) return;

            if (response?.error) {
                setScreenCreativesError(
                    t('sections.dashboard-individual.audit.creatives-load-error')
                );
            } else {
                setScreenCreatives(
                    Array.isArray(response?.data?.creatives)
                        ? response.data.creatives
                        : []
                );
                setScreenCreativesScreenSize(response?.data?.screen || null);
            }

            setIsFetchingScreenCreatives(false);
        };

        fetchScreenCreatives();

        return () => {
            mounted = false;
        };
    }, [id, isActive, auditEndpointScreenId, auditRequestConfig, refreshCount, t]);

    const metrics = auditData?.metrics || null;
    const latestPurchases = Array.isArray(auditData?.latestPurchases)
        ? auditData.latestPurchases
        : [];
    const latestPlays = Array.isArray(auditData?.latestPlays)
        ? auditData.latestPlays
        : [];
    const latestBids = Array.isArray(auditData?.latestBids)
        ? auditData.latestBids
        : [];
    const lossReasons = Array.isArray(auditData?.lossReasons)
        ? auditData.lossReasons
        : [];
    const mapsUrl = selectedScreen
        ? buildGoogleMapsUrl(selectedScreen.auditLat, selectedScreen.auditLng)
        : '';
    const imageUrl = selectedScreen
        ? toInlineScreenImageUrl(selectedScreen.auditImageUrl)
        : '';
    const campaignTitle = [campaign.brand, campaign.name]
        .filter(Boolean)
        .join(' | ');
    const campaignDateRange = `${formatCampaignDate(
        campaign.start_period
    )} - ${formatCampaignDate(campaign.end_period)}`;

    const closePurchaseModal = () => {
        if (!isGeneratingPurchase) {
            setIsPurchaseModalOpen(false);
        }
    };

    const refreshAuditData = () => {
        setNow(new Date());
        setRefreshCount((current) => current + 1);
        setSecondsToAuditRefresh(AUDIT_AUTO_REFRESH_SECONDS);
    };

    const notifyPurchaseError = (message) => {
        dispatch(
            notificationsActions.setNotification({
                type: 'error',
                title: t('sections.dashboard-individual.audit.purchase-error-title'),
                message:
                    message ||
                    t(
                        'sections.dashboard-individual.audit.purchase-error-description',
                        { screen: selectedScreen?.auditName || '-' }
                    ),
            })
        );
    };

    const confirmPurchase = async () => {
        if (purchaseModeWarning) {
            notifyPurchaseError(purchaseModeWarning);
            return;
        }

        if (!auditEndpointScreenId) {
            notifyPurchaseError(
                t('sections.dashboard-individual.audit.screen-id-unavailable')
            );
            return;
        }

        setIsGeneratingPurchase(true);

        const response = await statsService.generateCampaignScreenPurchase({
            campaignId: id,
            screenId: auditEndpointScreenId,
            requestConfig: auditRequestConfig,
        });

        setIsGeneratingPurchase(false);

        if (response?.error) {
            notifyPurchaseError(
                getPurchaseErrorMessage(
                    response,
                    t,
                    selectedScreen,
                    purchaseModeWarning
                )
            );
            return;
        }

        dispatch(
            notificationsActions.setNotification({
                type: 'success',
                title: t('sections.dashboard-individual.audit.purchase-success-title'),
                message: t(
                    'sections.dashboard-individual.audit.purchase-success-description',
                    { screen: selectedScreen?.auditName || '-' }
                ),
            })
        );
        setIsPurchaseModalOpen(false);
        refreshAuditData();
    };

    const isLoading = dashboard.isLoading || !campaignLoaded || !isSameCampaign;

    if (isLoading || !isActive) {
        return <Loading isLoading={true} />;
    }

    return (
        <>
            <Helmet>
                <title>{`${t(
                    'sections.dashboard-individual.audit.page-title'
                )} | ${campaign.id}`}</title>
            </Helmet>
            <div
                className={`fixed inset-0 w-full overflow-hidden bg-gray-100 ${styles.page}`}>
                <Header
                    currentSection={t('sections.dashboard.page-title')}
                    sectionPath="/dashboard"
                    subSection={`${campaign.brand ? campaign.brand + ' | ' : ''}${
                        campaign.name || ''
                    } | ${t('sections.dashboard-individual.audit.page-title')}`}
                    campaignId={campaign.id}
                    extraSection={campaign.status}
                />

                <main className={`workingArea mt-16 ${styles.main}`}>
                    <div className="mb-3 rounded bg-white px-4 py-3 shadow-sm">
                        <div className="flex items-start gap-3">
                            <button
                                type="button"
                                onClick={() => history.push(`/dashboard/${id}`)}
                                className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                                title={t('sections.dashboard-individual.audit.back')}
                                aria-label={t(
                                    'sections.dashboard-individual.audit.back'
                                )}>
                                <Icon name="angle-left" size="lg" />
                            </button>
                            <div className="min-w-0 flex-1">
                                <div className="font-light text-xs uppercase text-gray-500">
                                    {t('sections.dashboard-individual.audit.title')}
                                </div>
                                <h1 className="break-words text-xl font-semibold leading-snug text-gray-900">
                                    {campaignTitle || campaign.id}
                                </h1>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1">
                                        <Icon
                                            name="calendar"
                                            size="xs"
                                            className="mr-1 text-gray-400"
                                        />
                                        {campaignDateRange}
                                    </span>
                                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">
                                        {auditScreens.length}{' '}
                                        {t('common.words.screens')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {purchaseModeWarning && (
                        <div
                            role="status"
                            className="mb-3 flex-shrink-0 rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                            {purchaseModeWarning}
                        </div>
                    )}

                    <div
                        className={`grid grid-cols-1 gap-3 lg:grid-cols-10 ${styles.grid}`}>
                        <section
                            className={`module lg:col-span-3 ${styles.screenListPanel}`}>
                            <div className="mb-3 flex items-center justify-between">
                                <div className="font-light text-xs uppercase text-gray-700">
                                    {t(
                                        'sections.dashboard-individual.audit.screens-title'
                                    )}
                                </div>
                                <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
                                    {normalizedScreenSearch &&
                                        `${filteredAuditScreens.length} / `}
                                    {auditScreens.length}
                                </span>
                            </div>

                            <input
                                type="search"
                                value={screenSearch}
                                onChange={(event) =>
                                    setScreenSearch(event.target.value)
                                }
                                aria-label={t(
                                    'sections.dashboard-individual.audit.search-screens'
                                )}
                                placeholder={t(
                                    'sections.dashboard-individual.audit.search-screens'
                                )}
                                className="mb-3 w-full flex-shrink-0 rounded border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />

                            {isFetchingScreens && auditScreens.length === 0 ? (
                                <ScreenListSkeleton />
                            ) : auditScreens.length === 0 ? (
                                <div className="rounded border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    {t(
                                        'sections.dashboard-individual.audit.empty-screens'
                                    )}
                                </div>
                            ) : filteredAuditScreens.length === 0 ? (
                                <div
                                    role="status"
                                    className="rounded border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    {t(
                                        'sections.dashboard-individual.audit.no-matching-screens'
                                    )}
                                </div>
                            ) : (
                                <div
                                    className={`space-y-2 pr-1 ${styles.screenListScroll}`}>
                                    {filteredAuditScreens.map((screen) => {
                                        const selected =
                                            screen.auditId ===
                                            selectedScreen?.auditId;

                                        return (
                                            <article
                                                key={screen.auditKey}
                                                className={`rounded border px-3 py-2 transition-colors ${
                                                    selected
                                                        ? 'border-blue-400 bg-blue-50'
                                                        : 'border-gray-100 bg-white'
                                                }`}>
                                                <div className="flex items-center gap-2">
                                                    <Icon
                                                        name="tv"
                                                        size="sm"
                                                        className={
                                                            selected
                                                                ? 'text-blue-500'
                                                                : 'text-gray-400'
                                                        }
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <h2 className="truncate text-sm font-semibold text-gray-900">
                                                            {screen.auditName}
                                                        </h2>
                                                        <p className="truncate text-xs text-gray-500">
                                                            {screen.auditId}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedScreenId(
                                                                screen.auditId
                                                            )
                                                        }
                                                        title={t(
                                                            'sections.dashboard-individual.audit.view-audit'
                                                        )}
                                                        aria-label={t(
                                                            'sections.dashboard-individual.audit.view-audit'
                                                        )}
                                                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                                            selected
                                                                ? 'border-blue-500 bg-blue-500 text-white'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                                        }`}>
                                                        <Icon
                                                            name="eye"
                                                            size="sm"
                                                        />
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        <section
                            className={`module lg:col-span-7 ${styles.detailPanel}`}>
                            {selectedScreen ? (
                                <>
                                    <div className="flex flex-col gap-3 border-b border-gray-100 pb-3 md:flex-row md:items-start md:justify-between">
                                        <div className="min-w-0">
                                            <p className="font-light text-xs uppercase text-gray-500">
                                                {t(
                                                    'sections.dashboard-individual.audit.selected-screen'
                                                )}
                                            </p>
                                            <h2 className="mt-1 text-xl font-semibold text-gray-900">
                                                {selectedScreen.auditName}
                                            </h2>
                                            <p className="mt-1 text-sm text-gray-600">
                                                {selectedScreen.auditLocation ||
                                                    t(
                                                        'sections.dashboard-individual.audit.location-unavailable'
                                                    )}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 md:justify-end">
                                            <CommonButton
                                                styleType="primary"
                                                className={styles.generatePurchaseButton}
                                                disabled={
                                                    !auditEndpointScreenId ||
                                                    Boolean(purchaseModeWarning)
                                                }
                                                onClick={() =>
                                                    setIsPurchaseModalOpen(true)
                                                }>
                                                <span>
                                                    {t(
                                                        'sections.dashboard-individual.audit.generate-purchase'
                                                    )}
                                                </span>
                                            </CommonButton>
                                        </div>
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-5">
                                        <div className="xl:col-span-3">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={selectedScreen.auditName}
                                                    className={`w-full rounded border border-gray-100 object-cover ${styles.screenPreview}`}
                                                    loading="lazy"
                                                    referrerPolicy="no-referrer"
                                                />
                                            ) : (
                                                <div
                                                    className={`flex w-full items-center justify-center rounded border border-dashed border-gray-200 bg-gray-50 text-gray-400 ${styles.screenPreview}`}>
                                                    <div className="text-center">
                                                        <Icon
                                                            name="image"
                                                            size="2x"
                                                            className="mx-auto mb-2"
                                                        />
                                                        <p className="text-sm">
                                                            {t(
                                                                'sections.dashboard-individual.audit.image-unavailable'
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="xl:col-span-2">
                                            <div className="rounded border border-gray-100 bg-gray-50 p-4">
                                                <div className="font-light text-xs uppercase text-gray-500">
                                                    {t(
                                                        'sections.dashboard-individual.audit.location'
                                                    )}
                                                </div>
                                                <dl className="mt-3 space-y-3 text-sm">
                                                    <div>
                                                        <dt className="text-gray-500">
                                                            {t(
                                                                'sections.dashboard-individual.audit.latitude'
                                                            )}
                                                        </dt>
                                                        <dd className="font-semibold text-gray-900">
                                                            {selectedScreen.auditLat ??
                                                                '-'}
                                                        </dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-gray-500">
                                                            {t(
                                                                'sections.dashboard-individual.audit.longitude'
                                                            )}
                                                        </dt>
                                                        <dd className="font-semibold text-gray-900">
                                                            {selectedScreen.auditLng ??
                                                                '-'}
                                                        </dd>
                                                    </div>
                                                </dl>
                                                {mapsUrl ? (
                                                    <a
                                                        href={mapsUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-4 inline-flex items-center rounded bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow-sm hover:bg-blue-50">
                                                        <Icon
                                                            name="map-marker-alt"
                                                            size="sm"
                                                            className="mr-2"
                                                        />
                                                        {t(
                                                            'sections.dashboard-individual.audit.open-maps'
                                                        )}
                                                    </a>
                                                ) : (
                                                    <div className="mt-4 text-xs text-gray-500">
                                                        {t(
                                                            'sections.dashboard-individual.audit.maps-unavailable'
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-5">
                                        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {t(
                                                    'sections.dashboard-individual.audit.hour-range',
                                                    {
                                                        range:
                                                            hourRange.displayRange,
                                                    }
                                                )}
                                            </h3>
                                            {auditEndpointScreenId && (
                                                <div
                                                    className="inline-flex w-fit items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs text-blue-700"
                                                    aria-live="polite">
                                                    <Icon
                                                        name="sync"
                                                        size="sm"
                                                        className={`mr-2 ${
                                                            isAuditRequestInFlight
                                                                ? 'animate-spin'
                                                                : ''
                                                        }`}
                                                    />
                                                    {isAuditRequestInFlight
                                                        ? t(
                                                              'sections.dashboard-individual.audit.auto-refresh-updating'
                                                          )
                                                        : t(
                                                              'sections.dashboard-individual.audit.auto-refresh-info',
                                                              {
                                                                  interval:
                                                                      AUDIT_AUTO_REFRESH_SECONDS,
                                                                  seconds:
                                                                      secondsToAuditRefresh,
                                                              }
                                                          )}
                                                </div>
                                            )}
                                        </div>

                                        {isFetchingAuditData ? (
                                            <AuditMetricsSkeleton />
                                        ) : auditDataError ? (
                                            <div className="rounded border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                                                {auditDataError}
                                            </div>
                                        ) : (
                                            lossReasons.length > 0 && (
                                                <LossReasonsPanel
                                                    lossReasons={lossReasons}
                                                    t={t}
                                                />
                                            )
                                        )}

                                        {!isFetchingAuditData &&
                                            !auditDataError &&
                                            metrics && (
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                <AuditMetric
                                                    label={t(
                                                        'sections.dashboard-individual.audit.purchases'
                                                    )}
                                                    value={metrics.purchases}
                                                    records={latestPurchases}
                                                    timezone={timezone}
                                                    t={t}
                                                />
                                                <AuditMetric
                                                    label={t(
                                                        'sections.dashboard-individual.audit.plays'
                                                    )}
                                                    value={metrics.plays}
                                                    records={latestPlays}
                                                    timezone={timezone}
                                                    t={t}
                                                />
                                                <AuditMetric
                                                    label={t(
                                                        'sections.dashboard-individual.audit.bids-received'
                                                    )}
                                                    value={metrics.bidsReceived}
                                                    records={latestBids}
                                                    timezone={timezone}
                                                    t={t}
                                                    showCampaignInTooltip={false}
                                                />
                                                <AuditMetric
                                                    label={t(
                                                        'sections.dashboard-individual.audit.bids-received-yesterday'
                                                    )}
                                                    value={
                                                        metrics.bidsReceivedYesterday
                                                    }
                                                    timezone={timezone}
                                                    t={t}
                                                />
                                            </div>
                                        )}

                                    </div>

                                    <AuditCreativesPanel
                                        creatives={screenCreatives}
                                        screenSize={screenCreativesScreenSize}
                                        isLoading={
                                            isFetchingScreenCreatives ||
                                            (isFetchingScreens &&
                                                !auditEndpointScreenId)
                                        }
                                        error={screenCreativesError}
                                        t={t}
                                    />
                                </>
                            ) : (
                                <div className="rounded border border-dashed border-gray-200 p-8 text-center text-gray-500">
                                    {t(
                                        'sections.dashboard-individual.audit.select-screen-empty'
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>

            <Modal
                active={isPurchaseModalOpen}
                size="regular"
                className={styles.purchaseModal}
                toggler={closePurchaseModal}
                contentClassName={styles.purchaseModalContent}>
                <ModalHeader
                    toggler={closePurchaseModal}
                    className={styles.purchaseModalHeader}>
                    {t('sections.dashboard-individual.audit.confirm-title')}
                </ModalHeader>
                <ModalBody className={styles.purchaseModalBody}>
                    <p className="text-sm leading-5">
                        {t(
                            'sections.dashboard-individual.audit.confirm-description'
                        )}
                    </p>
                    {selectedScreen && (
                        <p className="mt-3 truncate rounded bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                            {selectedScreen.auditName}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className={styles.purchaseModalFooter}>
                    <CommonButton
                        styleType="cancel"
                        onClick={closePurchaseModal}>
                        <span>{t('common.buttons.cancel')}</span>
                    </CommonButton>
                    <CommonButton
                        styleType="primary"
                        className="ml-2"
                        disabled={
                            isGeneratingPurchase ||
                            !auditEndpointScreenId ||
                            Boolean(purchaseModeWarning)
                        }
                        onClick={confirmPurchase}>
                        <span>{t('common.buttons.confirm')}</span>
                    </CommonButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default CampaignAuditPage;
